/**
 * Recovery from a stale deploy.
 *
 * After a deploy the hashed chunks of the previous build are gone, so a tab
 * that was open before it fails the next lazy import. Reloading picks up the
 * new build. It reloads silently once; if the failure comes back within the
 * window, the reload did not fix it and the error screen takes over instead
 * of looping.
 */

const STORAGE_KEY = 'u360:chunk-reload-at';
const RETRY_WINDOW_MS = 30_000;

/** True when a failed import should trigger a reload, given the last one. */
export function shouldReloadForChunkError(lastReloadAt: number | null, now: number): boolean {
  return lastReloadAt === null || now - lastReloadAt > RETRY_WINDOW_MS;
}

/** Matches the messages browsers give when a lazy chunk cannot be loaded. */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /dynamically imported module|Importing a module script failed|Loading chunk|Unable to preload CSS/i.test(
    message,
  );
}

/**
 * Reloads the page unless it already did so moments ago. Returns whether it
 * reloaded. Without sessionStorage (blocked site data) there is no way to
 * tell a first failure from a loop, so it never reloads and the error screen
 * with its manual button stays the fallback.
 */
export function reloadForNewVersion(): boolean {
  let last: number | null = null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    last = raw ? Number(raw) : null;
  } catch {
    return false;
  }
  const now = Date.now();
  if (!shouldReloadForChunkError(last, now)) return false;
  try {
    sessionStorage.setItem(STORAGE_KEY, String(now));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}
